/**
 * The board pack (FB-7, screens §2.10): the round's report as six A4 sheets, composed from the same section components as the screens
 * (scoreboard, score story, line-by-line tables, books, rate view, calls, events). Print is the browser's "Save as PDF".
 */
import type { ContentCatalog, DraftView, MarketView, RoundReport, TeamState } from "@bsim/contracts";
import { meBank } from "../../cockpit/derive";
import { Alm } from "../../financials/Alm";
import { Books } from "../../financials/Books";
import { BOOK_TAB_IDS } from "../../financials/books";
import { GxDataTable } from "../../financials/DataTable";
import { fiscalYearLabel } from "../../format";
import { GxScoreboard } from "../../score/GxScoreboard";
import { GxPrintDesk, GxPrintPage } from "../../theme/GxPrintPage";
import { KpiInputsProvider, periodInputsView } from "../../theme/KpiInputs";
import { Calls } from "../Calls";
import { Events } from "../Events";
import { compareRows, type Statement } from "../lineCompare";
import { absolutePriceLine, priorPriceOf } from "../priceLine";
import { ResultsHero } from "../ResultsHero";
import { heroRankOf } from "../heroRank";
import { limitsBreached } from "../Breaches";
import { watchNextYear } from "../story";
import { MovedTables } from "./Moved";
import "./pack.css";

const PAGES = 6;
/** The Why column names causes only on lines whose gap to the twin is at least this many dollars. */
const WHY_MIN_USD = 5_000_000;
const STATEMENTS = [{ id: "balance", title: "Balance sheet, year end" }, { id: "income", title: "Income statement, for the year" }] as const satisfies readonly { id: Statement; title: string }[];
const BOOK_TITLES = { loans: "Loans", securities: "Securities", deposits: "Deposits", funding: "Funding" } as const;

export function Pack({ team, catalog, market, report, previous, draft, bankName: named }: {
  /** The bank's own name (the welcome card), which the team's label may not be. */ readonly bankName?: string | undefined;
  readonly team: TeamState; readonly catalog: ContentCatalog; readonly market: MarketView; readonly report: RoundReport; readonly previous: RoundReport | null; readonly draft: DraftView | null;
}) {
  const { roundNo } = report;
  const fy = fiscalYearLabel(catalog, roundNo);
  const bank = meBank(team, market);
  const bankName = named ?? bank?.name ?? team.team.name;
  const footer = `${bankName} · ${fy} board pack`;
  const isFinal = roundNo >= team.cohort.roundCount;
  const finalRankHidden = isFinal && !market.finalRankRevealed;
  const prior = priorPriceOf(previous, team.kpiHistory.find((h) => h.roundNo === roundNo - 1)?.kpis.price);
  const line = absolutePriceLine(report, prior);
  const yearEnd = report.quarterly.at(-1);
  const twin = report.twinQuarterly ?? [];
  const notes = isFinal ? [] : watchNextYear(report, catalog, draft, team.cohort.complexityByRound[roundNo + 1] ?? "executive", roundNo + 1);
  const sheet = (page: number, title: string, children: React.ReactNode, eyebrow?: string) =>
    <GxPrintPage page={page} of={PAGES} title={title} footer={footer} testId={`pack.page.${page}`} {...(eyebrow === undefined ? {} : { eyebrow })}>{children}</GxPrintPage>;
  return (
    <KpiInputsProvider value={periodInputsView(yearEnd?.kpiInputs, report.kpis)}>
      <GxPrintDesk testId="pack">
        {sheet(1, bankName, <>
          <p className="gx-small gx-muted">Round {roundNo} of {team.cohort.roundCount} · how {fy} went</p>
          <div className="gx-pack-hero" data-testid="pack.hero"><ResultsHero report={report} catalog={catalog} draft={draft} rank={heroRankOf(team, market, report, previous, roundNo)} firstRound={previous === null} onMoved={() => undefined} limits={{ breached: limitsBreached([report]), onSee: () => undefined }} {...(isFinal ? {} : { provisional: team.cohort.roundCount })} /></div>
          {line === null ? null : <p className="gx-small gx-muted">{line}</p>}
          {bank === undefined ? null : <GxScoreboard banks={[finalRankHidden ? { ...bank, rank: null } : bank]} youSeatId={team.team.seatId} variant="results" provisional={!isFinal} />}
        </>, "Board pack")}
        {STATEMENTS.map((s, i) => sheet(2 + i, `${s.title}: you against your twin`, twin.length === 0
          ? <p className="gx-muted">Your twin's year is not available for this round.</p>
          : <><GxDataTable testId={`pack.${s.id}`} caption={`${s.title}, you against your status-quo twin`} columns={["You", "Your twin", "Difference", "Why"]} rows={compareRows(report, twin, s.id, catalog, WHY_MIN_USD)} expandAll dense /><p className="gx-small gx-muted">The difference is you minus your twin, in $ millions. A line says why only when it is $5M or more apart.</p></>,
        fy))}
        {sheet(4, "Your books", <div className="gx-pack-books">{BOOK_TAB_IDS.map((tab) => (
          <div key={tab} className="gx-pack-block"><div className="gx-eyebrow">{BOOK_TITLES[tab]}</div>
            <Books tab={tab} products={yearEnd?.products ?? []} aociAfsUsd={yearEnd?.statements.balanceSheet.equity.aociAfsUsd ?? 0} wholesaleShare={yearEnd?.kpis.wholesaleShare} monthly={null} monthLabel={String} /></div>
        ))}</div>, `Year end, ${fy}`)}
        {sheet(5, "Your rate risk", <Alm alm={yearEnd?.alm ?? null} />, `Year end, ${fy}`)}
        {sheet(6, "What moved your numbers, the year's calls and next year", <>
          <MovedTables report={report} catalog={catalog} draft={draft} />
          <Calls report={report} draft={draft} catalog={catalog} />
          <Events report={report} isFinal={isFinal} />
          <div className="gx-pack-block"><div className="gx-eyebrow">{isFinal ? "After the last year" : "What to watch next year"}</div>{notes.length === 0
            ? <p className="gx-muted">{isFinal ? "That was the last year of the program." : "Nothing stands out for next year."}</p>
            : <ul className="gx-pack-watch" data-testid="pack.watch">{notes.map((n) => <li key={n}>{n}</li>)}</ul>}</div>
        </>, "Against your status-quo twin")}
      </GxPrintDesk>
    </KpiInputsProvider>
  );
}
